import React, { useCallback, useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

// ── Types (mirror the raw calendar-notches-state-prod row shape written by
// notches-signals/calendar-notches-engine/pkg/ddbWriter.js + returned as-is
// by api/auth/src/adminGetCalendarNotches.js) ────────────────────────────

interface CalendarNotchesSummaryRow {
  symbol: string;
  latest_sourceAsOf: string | null;
  spot?: number | null;
  r_squared?: number | null;
  alerts_at_1p0?: number;
  alerts_at_1p5?: number;
  n_expirations?: number;
}

interface CalendarNotchesSummary {
  symbols: CalendarNotchesSummaryRow[];
}

interface CalendarNotchesExpiration {
  expiration: string;
  dte: number;
  atm_iv: number;
  atm_iv_call: number | null;
  atm_iv_put: number | null;
  contract_count: number;
  fitted_iv: number;
  deviation_vol_pts: number;
  cross_cycle: boolean;
}

interface CalendarNotchesPair {
  t1: string;
  t2: string;
  dte1: number;
  dte2: number;
  sigma_calendar_variance: number;
  sigma_calendar_vega: number | null;
  fitted_forward: number;
  deviation_vol_pts: number;
  side: 'SELL_CALENDAR' | 'BUY_CALENDAR' | null;
  ref_strike: number | null;
}

interface CalendarNotchesRow {
  symbol: string;
  sourceAsOf: string;
  spot: number;
  cycle: string | null;
  is_settlement: boolean;
  expirations: CalendarNotchesExpiration[];
  fit: { a: number; b: number; r_squared: number; method: string } | null;
  pairs: CalendarNotchesPair[];
  alerts_at_1p0: number;
  alerts_at_1p5: number;
}

const SYMBOLS = ['SPX', 'SPY', 'QQQ', 'IWM', 'ES'];

// Dark-theme-friendly utility colors (matches AdminRegime.tsx)
const mutedTextColor = 'rgba(255, 255, 255, 0.5)';
const subduedBackground = 'rgba(255, 255, 255, 0.04)';

function fmtET(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return iso; }
}

function fmtNum(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '-';
  return v.toFixed(digits);
}

function devColor(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return 'inherit';
  const a = Math.abs(v);
  if (a > 1.5) return '#fca5a5';
  if (a > 1.0) return '#fcd34d';
  return 'inherit';
}

export const AdminCalendarNotches: React.FC = () => {
  const [summary, setSummary] = useState<CalendarNotchesSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [selected, setSelected] = useState<string>('SPX');
  const [latest, setLatest] = useState<CalendarNotchesRow | null>(null);
  const [latestLoading, setLatestLoading] = useState(false);
  const [latestError, setLatestError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await adminService.getCalendarNotchesSummary();
      setSummary(data);
    } catch (e: any) {
      setSummaryError(e?.response?.data?.error || e?.message || 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fetchLatest = useCallback(async (symbol: string) => {
    setLatestLoading(true);
    setLatestError(null);
    setLatest(null);
    try {
      const data = await adminService.getCalendarNotchesLatest(symbol);
      // Backend uses DynamoDBDocumentClient (auto-unmarshalled) -- `item` is
      // already a plain JS object, or null when no snapshot exists yet.
      setLatest(data?.item ?? null);
    } catch (e: any) {
      setLatestError(e?.response?.data?.error || e?.message || 'Failed to load latest snapshot');
    } finally {
      setLatestLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selected) fetchLatest(selected);
  }, [selected, fetchLatest]);

  return (
    <>
      <p style={{ color: mutedTextColor, fontSize: '14px', marginTop: 0 }}>
        Phase 1 (silent shadow): per-snapshot term-structure fits + calendar-spread deviations across
        SPX / SPY / QQQ / IWM / ES. Both 1.0-pt and 1.5-pt alert counts are recorded for review -- no
        alerts are sent to subscribers yet.
      </p>

      {summaryError && <p style={{ color: '#fca5a5' }}>Error: {summaryError}</p>}

      <div style={{ marginBottom: '20px' }}>
        <button onClick={fetchSummary} disabled={summaryLoading} className="admin-btn-primary">
          {summaryLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Per-symbol summary */}
      {summary?.symbols && (
        <section style={{ marginBottom: '32px' }}>
          <h3 style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px' }}>Per-symbol summary</h3>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th style={{ textAlign: 'right' }}>Spot</th>
                  <th style={{ textAlign: 'right' }}>R&sup2;</th>
                  <th style={{ textAlign: 'right' }}># exp</th>
                  <th style={{ textAlign: 'right' }}>&ge;1.0 pt</th>
                  <th style={{ textAlign: 'right' }}>&ge;1.5 pt</th>
                  <th>Latest snapshot (ET)</th>
                </tr>
              </thead>
              <tbody>
                {summary.symbols.map((r) => (
                  <tr
                    key={r.symbol}
                    onClick={() => setSelected(r.symbol)}
                    style={{
                      cursor: 'pointer',
                      background: r.symbol === selected ? 'rgba(102, 126, 234, 0.15)' : 'transparent',
                    }}
                  >
                    <td><strong style={{ color: 'rgba(255, 255, 255, 0.95)' }}>{r.symbol}</strong></td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(r.spot, 2)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(r.r_squared, 3)}</td>
                    <td style={{ textAlign: 'right' }}>{r.n_expirations ?? '-'}</td>
                    <td style={{ textAlign: 'right' }}>{r.alerts_at_1p0 ?? '-'}</td>
                    <td style={{ textAlign: 'right' }}>{r.alerts_at_1p5 ?? '-'}</td>
                    <td style={{
                      fontSize: '12px',
                      color: r.latest_sourceAsOf ? undefined : mutedTextColor,
                      fontStyle: r.latest_sourceAsOf ? 'normal' : 'italic',
                    }}>
                      {r.latest_sourceAsOf ? fmtET(r.latest_sourceAsOf) : 'no snapshot yet'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Drill-down: selected symbol's latest snapshot */}
      <section>
        <h3 style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px' }}>
          {selected} &mdash; latest term structure
        </h3>

        {latestLoading && <p style={{ color: mutedTextColor }}>Loading {selected}...</p>}
        {latestError && <p style={{ color: '#fca5a5' }}>Error: {latestError}</p>}

        {!latestLoading && !latestError && !latest && (
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            border: '1px dashed rgba(255, 255, 255, 0.1)',
            background: subduedBackground,
            color: mutedTextColor,
            fontStyle: 'italic',
            fontSize: '13px',
          }}>
            No snapshot yet for {selected}.
          </div>
        )}

        {latest && (
          <>
            <p style={{ color: mutedTextColor, fontSize: '13px' }}>
              As of {fmtET(latest.sourceAsOf)} &middot; spot {fmtNum(latest.spot, 2)} &middot; fit R&sup2;{' '}
              {fmtNum(latest.fit?.r_squared, 3)} ({latest.fit?.method || '-'}) &middot; cycle {latest.cycle || '-'}
              {latest.is_settlement ? ' (settlement)' : ''}
            </p>

            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Expiration</th>
                    <th style={{ textAlign: 'right' }}>DTE</th>
                    <th style={{ textAlign: 'right' }}>ATM IV</th>
                    <th style={{ textAlign: 'right' }}>Fitted</th>
                    <th style={{ textAlign: 'right' }}>&Delta; (vol pts)</th>
                    <th style={{ textAlign: 'right' }}># contracts</th>
                    <th>Cross-cycle</th>
                  </tr>
                </thead>
                <tbody>
                  {(latest.expirations || []).map((e) => (
                    <tr key={e.expiration}>
                      <td>{e.expiration}</td>
                      <td style={{ textAlign: 'right' }}>{e.dte}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(e.atm_iv * 100, 2)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(e.fitted_iv * 100, 2)}</td>
                      <td style={{ textAlign: 'right', color: devColor(e.deviation_vol_pts) }}>
                        {fmtNum(e.deviation_vol_pts, 2)}
                      </td>
                      <td style={{ textAlign: 'right' }}>{e.contract_count}</td>
                      <td>{e.cross_cycle ? 'yes' : 'no'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={{ color: 'rgba(255, 255, 255, 0.9)', margin: '24px 0 12px' }}>Adjacent-pair spread IV</h3>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Pair</th>
                    <th style={{ textAlign: 'right' }}>&sigma;_cal (var)</th>
                    <th style={{ textAlign: 'right' }}>&sigma;_cal (vega)</th>
                    <th style={{ textAlign: 'right' }}>Fitted fwd</th>
                    <th style={{ textAlign: 'right' }}>&Delta; (vol pts)</th>
                    <th>Side</th>
                  </tr>
                </thead>
                <tbody>
                  {(latest.pairs || []).map((p, i) => (
                    <tr key={`${p.t1}-${p.t2}-${i}`}>
                      <td>{p.t1} &rarr; {p.t2}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(p.sigma_calendar_variance * 100, 2)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {p.sigma_calendar_vega != null ? fmtNum(p.sigma_calendar_vega * 100, 2) : '-'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(p.fitted_forward * 100, 2)}</td>
                      <td style={{ textAlign: 'right', color: devColor(p.deviation_vol_pts) }}>
                        {fmtNum(p.deviation_vol_pts, 2)}
                      </td>
                      <td>{p.side ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </>
  );
};

export { SYMBOLS as CALENDAR_NOTCHES_SYMBOLS };
export default AdminCalendarNotches;
